<template>
  <v-app v-autofill-sync :theme="currentTheme">
    <page-loader v-if="dynamicLayout == null" />
    <component v-else :is="dynamicLayout">
      <router-view />
    </component>
    <template v-if="getErrors?.length">
      <custom-message
        v-for="(error, index) of getErrors"
        :key="index"
        :text="error.text"
        :style="{ bottom: `${index * 70}px` }"
        @close="cancelError(error)"
      />
    </template>

    <custom-message
      v-if="!getErrors?.length && getIsLoadingMessage"
      :text="getIsLoadingMessage"
      color="primary"
      icon="mdi-loading"
      icon-spin
    />
  </v-app>
</template>

<script setup>
import { storeToRefs } from 'pinia'


import { useAuthentication } from '@/stores/auth'
import { useLayout } from '@/stores/layout'
import { useSimulation } from '@/stores/simulation'
import { useResource } from '@/stores/resource'
import { useHome } from '@/stores/home'
import { useUser } from '@/stores/user'
import { useInstitution } from '@/stores/institution'
import { useLicense } from '@/stores/license'
import { useBundle } from '@/stores/bundle'
import { useFeedback } from '@/stores/feedback'
import UnusedLayout from '@/layouts/Unused.vue'

const route = useRoute()

const authenticationStore = useAuthentication()
const layoutStore = useLayout()
const simulationStore = useSimulation()
const resourceStore = useResource()
const homeStore = useHome()
const userStore = useUser()
const institutionStore = useInstitution()
const licenseStore = useLicense()
const bundleStore = useBundle()
const feedbackStore = useFeedback()

const { isAuthenticated, getFetchSessionFrequency, getFetchSessionInterval, getErrors: getErrorsAuthentication, getIsLoadingMessage: getIsLoadingMessageAuthentication } = storeToRefs(authenticationStore)
const { fetchSession, setFetchSessionInterval, cancelError: cancelErrorAuthentication } = authenticationStore

const { currentTheme } = storeToRefs(layoutStore)
const { resetStore } = layoutStore

const { getErrors: getErrorsSimulation, getIsLoadingMessage: getIsLoadingMessageSimulation } = storeToRefs(simulationStore)
const { cancelError: cancelErrorSimulation } = simulationStore

const { getErrors: getErrorsResource, getIsLoadingMessage: getIsLoadingMessageResource } = resourceStore
const { cancelError: cancelErrorResource } = resourceStore

const { getErrors: getErrorsHome, getIsLoadingMessage: getIsLoadingMessageHome } = storeToRefs(homeStore)
const { cancelError: cancelErrorHome } = resourceStore

const { getErrors: getErrorsUser, getIsLoadingMessage: getIsLoadingMessageUser } = storeToRefs(userStore)
const { cancelError: cancelErrorUser } = userStore

const { getErrors: getErrorsInstitution, getIsLoadingMessage: getIsLoadingMessageInstitution } = storeToRefs(institutionStore)
const { cancelError: cancelErrorInstitution } = institutionStore

const { getErrors: getErrorsLicense, getIsLoadingMessage: getIsLoadingMessageLicense } = storeToRefs(licenseStore)
const { cancelError: cancelErrorLicense } = licenseStore

const { getErrors: getErrorsBundle, getIsLoadingMessage: getIsLoadingMessageBundle } = storeToRefs(bundleStore)
const { cancelError: cancelErrorBundle } = bundleStore

const { getErrors: getErrorsFeedback, getIsLoadingMessage: getIsLoadingMessageFeedback } = storeToRefs(feedbackStore)
const { cancelError: cancelErrorFeedback } = feedbackStore

const getErrors = computed(() => [
  ...(getErrorsAuthentication?.value || []).map(e => ({ ...e, source: 'authentication' })),
  ...(getErrorsSimulation?.value || []).map(e => ({ ...e, source: 'simulation' })),
  ...(getErrorsResource?.value || []).map(e => ({ ...e, source: 'resource' })),
  ...(getErrorsHome?.value || []).map(e => ({ ...e, source: 'home' })),
  ...(getErrorsUser?.value || []).map(e => ({ ...e, source: 'user' })),
  ...(getErrorsInstitution?.value || []).map(e => ({ ...e, source: 'institution' })),
  ...(getErrorsLicense?.value || []).map(e => ({ ...e, source: 'license' })),
  ...(getErrorsBundle?.value || []).map(e => ({ ...e, source: 'bundle' })),
  ...(getErrorsFeedback?.value || []).map(e => ({ ...e, source: 'feedback' }))
])

const cancelError = (error) => {
  const cancelErrorMap = {
    authentication: cancelErrorAuthentication,
    simulation: cancelErrorSimulation,
    resource: cancelErrorResource,
    home: cancelErrorHome,
    user: cancelErrorUser,
    institution: cancelErrorInstitution,
    license: cancelErrorLicense,
    bundle: cancelErrorBundle,
    feedback: cancelErrorFeedback
  }
  cancelErrorMap[error.source]?.(error.key)
}

const getIsLoadingMessage = computed(() => {
  return [
    getIsLoadingMessageAuthentication?.value || null,
    getIsLoadingMessageSimulation?.value || null,
    getIsLoadingMessageResource?.value || null,
    getIsLoadingMessageHome?.value || null,
    getIsLoadingMessageUser?.value || null,
    getIsLoadingMessageInstitution?.value || null,
    getIsLoadingMessageLicense?.value || null,
    getIsLoadingMessageBundle?.value || null,
    getIsLoadingMessageFeedback?.value || null
  ].find(
    (m) => m != null && typeof m == 'string'
  )
})

const isDemoRoute = () => route.name === 'demo' || route.path.startsWith('/demo/')
const dynamicLayout = shallowRef(isDemoRoute() ? markRaw(UnusedLayout) : null)

watch([isAuthenticated, route], async (currentValue, oldValue) => {
  if (isDemoRoute()) {
    dynamicLayout.value = markRaw(UnusedLayout)
    return
  }
  if (oldValue[0] != null || !route.meta?.requiresAuthentication || ['404', 'welcome'].includes(route?.name)) {
    if (currentValue[0]) {
      setFetchSessionInterval(setInterval(
        fetchSession,
        getFetchSessionFrequency.value
      ))
      dynamicLayout.value = markRaw((await import('@/layouts/Authenticated.vue')).default)
    } else {
      setFetchSessionInterval(null)
      dynamicLayout.value = markRaw((await import('@/layouts/Unauthenticated.vue')).default)
    }
    // Keep the persisted page on session restore; logout still clears it via resetStore(false).
    if (!oldValue[0] && currentValue[0]) resetStore(true)
    else if (oldValue[0] != null) resetStore(currentValue[0])
    if (currentValue[1].name == '404' && currentValue[0]) resetStore(false)
  }
}, { deep : true})

onBeforeUnmount(() => {
  if (getFetchSessionInterval.value != null)
    setFetchSessionInterval(null)
  dynamicLayout.value = null
})

onMounted(() => {
  if (isDemoRoute())
    dynamicLayout.value = markRaw(UnusedLayout)
})
</script>
